import { h, Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import clsx from 'clsx';
import Price from '../product/product-price';
import { getProductsByTag } from '../utils/storefront/get-products';
import Image from '../utils/image';
import { elevarTracking } from '../utils/helpers';

const ProductRecommendationSlide = ({ product, customerId }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [variantSelectorIsOpen, setVariantSelectorIsOpen] = useState(false);

  useEffect(() => {
    if (!product) return;

    const fetchData = async () => {
      const getFamilyTag = product.tags.find((tag) => tag.includes('family:'));
      const initialProductRequest = await getProductsByTag(getFamilyTag);
      const productData = initialProductRequest.data.products.nodes;

      const initialProduct = productData.find(
        (p) =>
          product.id === parseInt(p.id.split('gid://shopify/Product/')[1], 10),
      );

      if (initialProduct) {
        setAllProducts(productData);
        setSelectedProduct(initialProduct);
        setSelectedVariant(initialProduct.variants.edges[0].node);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSwatchClick = (product) => {
    setSelectedProduct(product);
    setSelectedVariant(
      product.variants.edges.find((v) => v.node.title === selectedVariant.title)
        .node,
    );
  };

  const handleSizeSelect = (variant) => {
    setSelectedVariant(variant);
    setVariantSelectorIsOpen(false);
  };

  const addToCart = () => {
    if (selectedVariant) {
      const addToCartEvent = new CustomEvent('cart:add', {
        detail: {
          payload: {
            id: selectedVariant.id,
            quantity: 1,
          },
        },
      });

      let compareAtPrice = `${selectedProduct.compare_at_price}` || '0.00';
      if (compareAtPrice === 'undefined') compareAtPrice = '0.00';

      const products = [
        {
          id: selectedVariant.sku,
          name: selectedVariant.product.title,
          brand: selectedProduct.vendor,
          category: selectedProduct.productType,
          variant: selectedVariant.title,
          price: `${selectedVariant.priceV2.amount}`,
          quantity: '1',
          position: '1',
          list: document.referrer,
          product_id: selectedProduct.id.split('/Product/')[1],
          variant_id: selectedVariant.id.split('/ProductVariant/')[1],
          compare_at_price: compareAtPrice,
          image: selectedVariant.image.src || '',
          url: window.location.href,
        },
      ];

      elevarTracking({
        customerId,
        products,
        event: 'dl_add_to_cart',
      });

      document.dispatchEvent(addToCartEvent);
    }
  };

  if (!selectedProduct) return null;

  return (
    <>
      <p role="heading" class="sr-only">
        product recommendation: {selectedProduct.title}
      </p>
      <div class="flex mb-[.625rem]">
        <a href={`/products/${selectedProduct.handle}`} className="mr-3">
          <div className="w-24 h-auto bg-bg-6">
            <Image
              {...selectedProduct.images.edges[0].node}
              width={98}
              height={78}
            />
          </div>
        </a>
        <div class="flex flex-col">
          <a href={`/products/${selectedProduct.handle}`}>
            <p className="mb-1 heading-6 font-black text-sm desktop:text-base leading-[18px] desktop:leading-[22px]">
              {selectedProduct.title}
            </p>
          </a>
          <Price
            currentPrice={selectedVariant.priceV2.amount}
            currentComparePrice={selectedVariant.compareAtPrice?.amount}
            containerClass={'!justify-start'}
            classes={'font-m-12'}
          />
          {selectedProduct.productType === 'Apparel' && (
            <>
              <p className="my-1 font-bold font-m-10 desktop:text-xs text-secondary-1">
                {selectedProduct.colorName?.value}
              </p>
              <p className="font-bold font-m-10 desktop:text-xs text-secondary-1">
                {selectedVariant.title}
              </p>
            </>
          )}
        </div>
      </div>
      {selectedProduct.productType === 'Apparel' && (
        <div>
          <div className="mb-[.625rem]">
            <p role="heading" className="mb-2 font-bold font-m-12">
              COLOR:
            </p>
            <ul className="flex gap-1">
              {allProducts.map((product) => (
                <li
                  key={product.pid}
                  data-product-id={product.pid}
                  role="presentation"
                  className={`hover:cursor-pointer w-10 h-10 ${
                    selectedProduct.apparelColorName?.value
                    === product.apparelColorName?.value
                      ? 'border-2 border-grey-900'
                      : ''
                  }`}
                  style={{ backgroundColor: product.apparelColor?.value }}
                  title={product.colorName}
                  onClick={() => handleSwatchClick(product)}
                />
              ))}
            </ul>
          </div>
          <button
            onClick={() => setVariantSelectorIsOpen(!variantSelectorIsOpen)}
            class={clsx(
              variantSelectorIsOpen
                && 'before:[transform:rotateX(180deg)] after:[transform:rotateX(180deg)]',
              'btn-primary-stroke-arrow-d mr-3 py-3 group w-full border-[#EAEAEB] before:top-[9px] after:top-[9px]',
            )}
          >
            <span class="btn-arrow-d-separator-black bg-[#EAEAEB] group-hover:bg-accent-1" />
            Select Size
          </button>
          <ul
            data-size-options
            className={clsx(
              variantSelectorIsOpen ? 'h-fit' : 'h-0',
              'overflow-y-hidden mb-[.625rem] border-x border-b border-[#EAEAEB]',
            )}
          >
            {selectedProduct.variants.edges.length
              && selectedProduct.variants.edges.map((variant) => (
                <li
                  key={variant.node.id}
                  data-variant-id={variant.node.id}
                  data-option={variant.node.title}
                  onClick={() => handleSizeSelect(variant.node)}
                  className={`
                    hover:cursor-pointer hover:bg-bg-6
                    font-m-12 font-bold
                    pl-5 py-3
                    [&:not(:last-child)]:border-b border-[#EAEAEB]
                    tracking-[0.84px]
                    ${
                selectedVariant.title === variant.node.title
                  ? 'bg-bg-6'
                  : ''
                }
                  `}
                >
                  {variant.node.title}
                </li>
              ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        className="btn-primary-fill flex items-center justify-center mb-[7px] laptop:mb-0 w-full"
        onClick={() => addToCart()}
      >
        ADD TO BAG
      </button>
    </>
  );
};

export default ProductRecommendationSlide;
